/**
 * Utility functions for transforming and enhancing lead data
 */

const KPI_FIELDS = [
	{ value: 'revenue', estimate: 'revenueIsEstimate' },
	{ value: 'employeeCount', estimate: 'employeeCountIsEstimate' },
	{ value: 'earnings', estimate: 'earningsIsEstimate' },
	{ value: 'balanceTotal', estimate: 'balanceTotalIsEstimate' },
	{ value: 'equity', estimate: 'equityIsEstimate' },
	{ value: 'liabilities', estimate: 'liabilitiesIsEstimate' },
];

/**
 * Determines the most recent year with a non-null value for each KPI field.
 * Returns null if no financial data is available.
 * @param {Array<Object>|null} financials - Array of financial data objects with year property
 * @returns {Object|null} Object with the latest values for revenue, employeeCount, earnings, balanceTotal, equity, liabilities (each with estimate flag and year)
 */
export const buildLatestFinancials = (financials) => {
	if (!financials || financials.length === 0) return null;

	const sorted = [...financials].sort((a, b) => (b.year || 0) - (a.year || 0));
	const result = {};

	for (const { value, estimate } of KPI_FIELDS) {
		const match = sorted.find((f) => f[value] !== null && f[value] !== undefined);
		if (match) {
			result[value] = match[value];
			result[estimate] = match[estimate];
			result[`${value}Year`] = match.year;
		} else {
			result[value] = null;
			result[estimate] = false;
			result[`${value}Year`] = null;
		}
	}

	return result;
};

/**
 * Enriches leads with AI analysis data (score and criteria).
 * @param {Array<Object>} leads - Array of lead objects
 * @param {Array<Object>} aiAnalyses - Array of AI analysis objects with companyId mapping
 * @returns {Array<Object>} Leads with additional aiScore and aiCriteria properties
 */
export const enhanceLeadsWithAI = (leads, aiAnalyses) => {
	// Create AI analysis lookup map
	const aiAnalysisMap = aiAnalyses.reduce((acc, analysis) => {
		acc[analysis.companyId] = analysis;
		return acc;
	}, {});

	// Enhance leads with AI analysis data
	return leads.map((lead) => {
		const analysis = aiAnalysisMap[lead.id];
		return {
			...lead,
			aiScore: analysis?.score,
			aiCriteria: analysis?.extractedCriteria || analysis?.input?.criteriaText,
		};
	});
};

/**
 * Checks whether at least one lead has an AI score.
 * @param {Array<Object>} leads - Array of lead objects
 * @returns {boolean} true if at least one lead has a defined aiScore
 */
export const hasAIScores = (leads) => {
	return leads.some((lead) => lead.aiScore !== null && lead.aiScore !== undefined);
};
